import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { Image } from "@/components/atoms";
import type { CategoryNavigationProps } from "./CategoryNavigation.types";
import { useMediaQuery } from "@mui/material";
import { imagorService } from "@/helpers";

/**
 * Category Navigation Component
 */
export const CategoryNavigation: React.FC<CategoryNavigationProps> = React.memo(
  ({ categories, selectedCategoryId, onCategorySelect, loading = false }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("x2l"));

    // Ref for the scroll container to enable auto-scrolling
    const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);

    // Refs for individual category items to calculate scroll position
    const categoryItemRefs = React.useRef<
      Record<string, HTMLDivElement | null>
    >({});

    // Flag to prevent onCategorySelect during auto-scroll
    const autoScrollingRef = React.useRef(false);

    // Auto-scroll to active category
    React.useEffect(() => {
      if (!selectedCategoryId || !scrollContainerRef.current) return;

      const activeElement = categoryItemRefs.current[selectedCategoryId];
      if (!activeElement) return;

      // Add debounce to prevent excessive scrolling during rapid updates
      const timeoutId = setTimeout(() => {
        const container = scrollContainerRef.current;
        if (!container || !activeElement) return;

        const elementRect = activeElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // Calculate element position relative to container
        const elementLeft =
          elementRect.left - containerRect.left + container.scrollLeft;
        const containerPadding = isMobile ? 16 : 24; // Match the container padding

        // Always scroll to position active category at the very left (after padding)
        const targetScrollLeft = Math.max(0, elementLeft - containerPadding);

        // Only scroll if there's a significant difference (avoid micro-adjustments)
        const currentScrollLeft = container.scrollLeft;
        const scrollDifference = Math.abs(targetScrollLeft - currentScrollLeft);

        if (scrollDifference > 5) {
          // 5px threshold to avoid unnecessary scrolling
          autoScrollingRef.current = true;

          container.scrollTo({
            left: targetScrollLeft,
            behavior: "smooth",
          });

          // Reset auto-scrolling flag after animation completes
          setTimeout(() => {
            autoScrollingRef.current = false;
          }, 300); // Reduced timeout for more responsive feel
        }
      }, 50); // 50ms debounce for auto-scroll

      return () => clearTimeout(timeoutId);
    }, [selectedCategoryId, isMobile]);

    // Handle category click
    const handleCategoryClick = React.useCallback(
      (category: any) => {
        // Don't trigger onCategorySelect during auto-scroll
        if (autoScrollingRef.current) return;

        // Call the provided callback if available
        onCategorySelect?.(category);
      },
      [onCategorySelect],
    );

    // Проверяем, есть ли хотя бы у одной категории изображение
    const hasAnyImages = categories.some((category) => category.imageUrl);

    // Transform category image URLs with imagor service
    const transformedImageUrls = useMemo(() => {
      const urlMap = new Map<string, string | null>();
      categories.forEach((category) => {
        const imageUrl = category.imageUrl;
        if (!imageUrl) {
          urlMap.set(category.id, null);
          return;
        }
        try {
          const transformedUrl = imagorService.transformUrl(imageUrl, {
            width: 40,
            height: 40,
            smart: true,
          });
          urlMap.set(category.id, transformedUrl);
        } catch (error) {
          console.error("Error transforming category image URL:", error);
          urlMap.set(category.id, imageUrl);
        }
      });
      return urlMap;
    }, [categories]);

    return (
      <Box>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 0.75, // 6px
              alignItems: "flex-start",
              justifyContent: "flex-start",
              pb: 1.5,
              pt: 1.5,
              pl: 3.125, // 25px
              pr: 3.125, // 25px
            }}
          >
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton
                key={index}
                variant="rectangular"
                sx={{
                  borderRadius: 1.25, // 10px
                  flexShrink: 0,
                  minHeight: 71,
                  minWidth: 80,
                  width: 10,
                }}
              />
            ))}
          </Box>
        ) : (
          <Box
            ref={scrollContainerRef}
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 0.75, // 6px
              alignItems: "center",
              justifyContent: "flex-start",
              pb: 1.5,
              pt: 0,
              px: {
                xs: 2,
                x2l: 3,
              },
              overflowX: "auto",
              "&::-webkit-scrollbar": {
                height: 0.5, // 4px
              },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "primary.main",
                borderRadius: 0.25, // 2px
              },
            }}
          >
            {categories.map((category) => {
              const isActive = selectedCategoryId === category.id;
              const transformedImageUrl = transformedImageUrls.get(category.id);

              return (
                <Box
                  key={category.id}
                  ref={(el: HTMLDivElement | null) => {
                    categoryItemRefs.current[category.id] = el;
                  }}
                  onClick={() => handleCategoryClick(category)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    bgcolor: isActive ? "background.paper" : "transparent",
                    borderRadius: 2.5,
                    cursor: "pointer",
                    border: "1px solid",
                    borderColor: isActive ? "background.default" : "divider",
                    px: 1,
                    py: 0.5,
                    minWidth: 82,
                    transition: theme.transitions.create(
                      ["background-color", "border-color", "color"],
                      {
                        duration: theme.transitions.duration.short,
                      },
                    ),
                    "&:hover": {
                      bgcolor: isActive ? "action.focus" : "action.hover",
                    },
                    flexShrink: 0,
                    overflow: "hidden",
                  }}
                >
                  {hasAnyImages && (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        mr: 0.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Image
                        src={transformedImageUrl ?? category.imageUrl ?? ""}
                        alt={category.name}
                        width={40}
                        height={40}
                        showLoadingState={true}
                        sx={{
                          objectFit: "cover",
                          borderRadius: 20,
                        }}
                      />
                    </Box>
                  )}
                  <Typography
                    sx={{
                      textAlign: "center",
                      fontWeight: 600,
                      fontSize: 14,
                      maxWidth: "100%",
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 2,
                    }}
                  >
                    {category.name}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );
  },
);

CategoryNavigation.displayName = "CategoryNavigation";
